import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { FrameworkResponse } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { HoverTip } from "@/components/ui/HoverTip";
import { Spinner } from "@/components/ui/Spinner";
import { useProposeFramework } from "@/hooks/use-dd-framework";
import { proposalRunning } from "./ProposalState";

const KEPT = "Nothing you wrote, moved or removed changes.";
const FAILED = "The framework could not be proposed again. Nothing changed.";

/** Says how the run this act started ended, once the stream lands it. */
function useOutcome(proposal: FrameworkResponse["proposal"]) {
  const askedRef = useRef<string | null>(null);
  useEffect(() => {
    if (askedRef.current === null || proposal.run_id !== askedRef.current) {
      return;
    }
    if (proposal.status === "complete") {
      askedRef.current = null;
      toast.success(
        "Patent Studio proposed the framework again. Your edits are kept.",
      );
    } else if (proposal.status === "failed") {
      askedRef.current = null;
      toast.error(FAILED);
    }
  }, [proposal]);
  return askedRef;
}

/**
 * Asks Praxis for its sections again from the sources the plan is read from. What the attorney
 * wrote, moved or removed stays where it is; over a confirmed framework it asks first, since a
 * new proposal lapses the confirmation.
 */
export function ProposeAgain({
  taskId,
  data,
}: {
  taskId: string;
  data: FrameworkResponse;
}) {
  const propose = useProposeFramework(taskId, FAILED);
  const askedRef = useOutcome(data.proposal);
  const [asking, setAsking] = useState(false);
  const running = proposalRunning(data.proposal) || propose.isPending;
  const start = () => {
    propose.mutate(undefined, {
      onSuccess: (served) => {
        askedRef.current = served.proposal.run_id ?? null;
      },
    });
  };
  return (
    <>
      <HoverTip
        tone="dark"
        placement="above"
        tip={`Patent Studio proposes its sections again from the sources above. ${KEPT}`}
      >
        {({ triggerProps }) => (
          <Button
            {...triggerProps}
            variant="secondary"
            size="dense"
            disabled={running}
            onClick={() => {
              if (data.confirmed_current) setAsking(true);
              else start();
            }}
          >
            {running ? <Spinner size="sm" /> : <Sparkles aria-hidden="true" />}
            {running ? "Proposing…" : "Propose again"}
          </Button>
        )}
      </HoverTip>
      <ConfirmDialog
        open={asking}
        title="Propose the framework again?"
        description={`Patent Studio replaces its own sections and subsections with a new proposal. ${KEPT} The framework is no longer confirmed, so you confirm it again.`}
        actionLabel="Propose again"
        cancelLabel="Keep this framework"
        onCancel={() => {
          setAsking(false);
        }}
        onConfirm={() => {
          setAsking(false);
          start();
        }}
      />
    </>
  );
}
